// Reusable DPR-aware canvas surface (owned by the web:charts agent).
//
// Many of our charts are histograms / strips / heatmaps that are simplest and
// fastest as direct 2D canvas draws rather than a charting library. This
// component owns the boilerplate: it sizes the backing store to the container
// width at the device pixel ratio, keeps the CSS size in logical pixels, and
// re-runs the caller's `draw(ctx, w, h)` on mount, resize, and whenever `draw`
// changes. The ctx passed to `draw` is pre-scaled, so callers work purely in
// logical pixels.

import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useChartTheme } from './palette';

/** Centered muted note for empty / not-yet-measured chart states. */
export function EmptyNote({ label }: { label: string }) {
  const { palette } = useChartTheme();
  return (
    <div
      style={{
        padding: '24px 12px',
        textAlign: 'center',
        color: palette.textMuted,
        fontSize: 13,
      }}
    >
      {label}
    </div>
  );
}

export interface LegendItem {
  label: string;
  color: string;
  /** Optional hatch/tick glyph so colour is not the only signal. */
  glyph?: 'solid' | 'hatch' | 'tick' | 'dash';
}

/** Small wrap-friendly legend row shared by canvas charts. */
export function ChartLegend({ items }: { items: LegendItem[] }) {
  const { palette } = useChartTheme();
  return (
    <ul
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px 14px',
        listStyle: 'none',
        margin: '6px 0 0',
        padding: 0,
        fontSize: 12,
        color: palette.textMuted,
      }}
    >
      {items.map((it) => (
        <li key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              borderRadius: 2,
              background:
                it.glyph === 'hatch'
                  ? `repeating-linear-gradient(45deg, ${it.color} 0 2px, transparent 2px 4px)`
                  : it.color,
              border: it.glyph === 'dash' ? `1px dashed ${it.color}` : 'none',
              boxSizing: 'border-box',
            }}
          />
          <span>{it.label}</span>
        </li>
      ))}
    </ul>
  );
}

export interface CanvasSurfaceProps {
  /** Logical (CSS) height in pixels. */
  height: number;
  /** Draw callback; ctx is already translated/scaled for the device pixel ratio. */
  draw: (ctx: CanvasRenderingContext2D, width: number, height: number) => void;
  className?: string;
  /** Accessible label for the rendered figure. */
  ariaLabel?: string;
  /** Optional pointer-move handler receiving logical coordinates. */
  onPointer?: (x: number, y: number, width: number, height: number) => void;
  /** Called when the pointer leaves the surface. */
  onPointerLeave?: () => void;
}

/** Off-screen-safe canvas that redraws on resize and prop change. */
export function CanvasSurface({
  height,
  draw,
  className,
  ariaLabel,
  onPointer,
  onPointerLeave,
}: CanvasSurfaceProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || width <= 0) return;
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    draw(ctx, width, height);
  }, [width, height, draw]);

  useEffect(() => {
    render();
  }, [render]);

  const handleMove = useCallback(
    (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (!onPointer) return;
      const rect = e.currentTarget.getBoundingClientRect();
      onPointer(e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height);
    },
    [onPointer],
  );

  return (
    <div ref={wrapRef} className={className} style={{ width: '100%' }}>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={ariaLabel}
        style={{ display: 'block', width: '100%', height }}
        onPointerMove={onPointer ? handleMove : undefined}
        onPointerLeave={onPointerLeave}
      />
    </div>
  );
}
